/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { createTheme, Input as MInput, InputAdornment, ThemeProvider } from '@mui/material'
import block from 'bem-cn'

import { ReactComponent as CloseIcon } from 'shared/img/close-icon.svg'
import { ReactComponent as SearchSVG } from 'shared/img/search.svg'

import { localeErrorStatusMessageKeysType } from 'shared/locale/en-US'

import { selectLocaleDict } from 'features/locale/selectors'

import { ReactComponent as EyeIcon } from './img/eye-icon.svg'
import { ReactComponent as EyeSlashIcon } from './img/eye-icon-slash.svg'

import './Input.scss'

interface IInputProps {
  label?: string
  placeholder?: string
  value: string | number
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  name?: string
  isRequired?: boolean
  type?: string
  errors?: any
  error?: string | boolean
  isAllowedToDelete?: boolean
  deleteInputCallback?: (e: any) => void
  rounded?: boolean
  bottomText?: string | boolean
  height?: string
  svg?: { icon: any } | null
  bgColor?: string
  isSearch?: boolean
  borderPar?: string
  [x: string]: any
}

const b = block('input')

export const Input: React.FC<IInputProps> = ({
  placeholder,
  value,
  type = 'text',
  onChange,
  isAllowedToDelete = false,
  isRequired = false,
  errors,
  error,
  deleteInputCallback,
  rounded = false,
  borderPar = '1px solid #575757',
  bottomText,
  height = '100%',
  svg,
  isSearch,
  bgColor = '#1E1E1E',
  ...props
}) => {
  const isPasswordInput = useMemo(() => type === 'password', [type])

  const isShowEndAdornment = useMemo(() => isPasswordInput || isAllowedToDelete, [isAllowedToDelete, isPasswordInput])

  const padding = useMemo(() => {
    if (isShowEndAdornment) {
      return '0 36px 0 16px'
    }

    if (isSearch) {
      return '0 16px 0 36px'
    }

    return '0 16px'
  }, [isSearch, isShowEndAdornment])

  const theme = createTheme({
    components: {
      MuiInput: {
        styleOverrides: {
          root: {
            background: bgColor,
            border: borderPar,
            borderRadius: 16,
            height,
            maxHeight: '100%',
            minHeight: '100%',
            color: '#fff',
            padding: 0,
            overflow: 'hidden',
            textTransform: 'none',
            fontVariant: 'inherit',
            fontStretch: 'inherit',
            fontSize: 14,
            lineHeight: 1,
            transition: 'all .3s ease',
            '&.Mui-disabled': {
              borderRadius: '12px',
            },
          },
          input: {
            height: '100%',
            padding,
            borderRadius: rounded ? '16px' : '12px',
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            cursor: 'pointer',
            border: 'none',
          },
        },
      },
    },
  })

  const errorLocale = useSelector(selectLocaleDict, shallowEqual).errorStatusMessage

  const [visible, setVisible] = React.useState(false)

  const inputType = useMemo(
    () => (isPasswordInput ? (visible ? 'text' : 'password') : type),
    [type, isPasswordInput, visible],
  )
  const changeVisiblePassword = () => {
    setVisible(!visible)
  }

  const currErr = useMemo(() => {
    const { name } = props
    return errors?.listOfErrors.find((err: any) => err.field === name)
  }, [errors, props])

  const onChangeCallback = useCallback(
    e => {
      onChange(e)
    },
    [onChange],
  )

  return (
    <div className={b('wrapper', { isFlex: !!svg })}>
      {svg && (
        <div className={b('left-icon-wrapper')} style={{ height }}>
          <svg.icon className={b('left-icon')} />
        </div>
      )}
      <ThemeProvider theme={theme}>
        <MInput
          fullWidth
          autoComplete="off"
          disableUnderline
          type={inputType}
          value={value}
          onChange={onChangeCallback}
          placeholder={placeholder}
          required={isRequired}
          className={b().toString()}
          inputProps={{ autoCapitalize: 'none' }}
          startAdornment={
            isSearch && (
              <InputAdornment position="start">
                <SearchSVG />
              </InputAdornment>
            )
          }
          endAdornment={
            (isPasswordInput && (
              <InputAdornment position="end" onClick={changeVisiblePassword}>
                {visible ? <EyeIcon /> : <EyeSlashIcon />}
              </InputAdornment>
            )) ||
            (isAllowedToDelete && (
              <InputAdornment position="end" onClick={deleteInputCallback}>
                <CloseIcon />
              </InputAdornment>
            ))
          }
          {...props}
        />
        <div className={b('error')}>
          {error || errorLocale[currErr?.code as localeErrorStatusMessageKeysType]}&nbsp;
        </div>
        <div className={b('bottom-text')}>{bottomText}</div>
      </ThemeProvider>
    </div>
  )
}
